import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Text } from '@uhg-abyss/web/ui/Text';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCoverageType } from '../../../hooks/useCoverageType';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { FacilityLocationsResponse } from '../../../models/FacilityDetails';
import { ProviderLocation } from '../../../models/ProviderDetails';
import { isProviderStartDateInFuture } from '../../../utils/date.utils';
import { shouldUseGehaV2Styling } from '../../../utils/gehaThemeUtils';
import { shortenZipCode } from '../../../utils/providerDetails.utils';
import { County, ReverseDentalCodes } from '../../Constants';
import { ConstantsLagoon } from '../../ConstantsLagoon';
import { InNetworkBadgeWithDate } from '../../InNetworkBadge/InNetworkBadge';
import { TerminationBadge } from '../../TerminationBadge/TerminationBadge';
import { handleLinkAndModalTrack } from '../../Utils/adobeTrackUtils';
import {
  StyledAddressLine,
  StyledLocationBox,
  StyledLocationBoxContainer,
  StyledLocationsDropdownOption,
  StyledLocationsPopover,
} from './PopOver.style';

type Props = {
  locationsDetails: ProviderLocation[] | FacilityLocationsResponse[];
  locationsContentForPopOver: React.ReactNode | React.ReactFragment;
  isOpen: boolean;
  setIsOpen: (a: boolean) => void;
  selectedLocation: any | {};
  headingId?: string;
};

export const PopOver = ({
  locationsDetails,
  locationsContentForPopOver,
  isOpen,
  setIsOpen,
  selectedLocation,
  headingId,
}: Props) => {
  const { t } = useTranslation();
  const { address, distance } = selectedLocation;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const postalCode = shortenZipCode(address?.postalCode);
  const county =
    address?.district && !address.district.toLowerCase().includes('county')
      ? `${address.district} ${County},`
      : address?.district
      ? `${address.district},`
      : '';

  const [
    isCountyLabelEnabled,
    displayInNetworkBadgeEnabled,
    isDentalLocationNameEnable,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_COUNTY_LABEL_IN_LOCATION_POPOVER,
    ConstantsLagoon.FEATURE_FLAGS.DISPLAY_IN_NETWORK_BADGE_FUTURE_START_DATE,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_DENTAL_LOCATION_NAME,
  ]);

  const isThemeGehaV2Enabled = shouldUseGehaV2Styling();

  const coverageType = useCoverageType();

  const dentalCoverage = ReverseDentalCodes.includes(coverageType);

  const displayLocationName =
    dentalCoverage && isDentalLocationNameEnable ? address.locationName : '';
  const displayInNetworkBadge = isProviderStartDateInFuture(
    selectedLocation?.network?.start
  );

  useEffect(() => {
    if (isOpen) {
      const linkName: string = 'change location popover';
      const linkLocation: string = `body:locations`;
      const modalName: string = 'change location';
      handleLinkAndModalTrack(linkName, linkLocation, modalName);
    } else {
      setIsModalOpen(false);
    }
  }, [isOpen]);

  const isGEHAV2 = shouldUseGehaV2Styling();
  const isGehaTheme = isThemeGehaV2Enabled ? isGEHAV2 : '';

  return locationsDetails.length > 1 ? (
    <StyledLocationBoxContainer>
      <StyledLocationsPopover
        aria-describedby="changeLocation"
        content={locationsContentForPopOver}
        heading={
          <Text
            css={{
              fontWeight: '700',
              fontStyle: 'Normal',
              fontSize: '25.63px',
              marginLeft: '8px',
              marginRight: '40px',
            }}
            data-auto-testid="change-location-heading"
            data-testid="change-location-heading"
          >
            <span id={headingId}>
              {t('PROVIDER_DETAILS.LOCATIONS_TAB.CHANGE_LOCATION')}
            </span>
          </Text>
        }
        onOpenChange={() => {
          if (!isOpen && !isModalOpen) {
            setIsOpen(true);
            setIsModalOpen(true);
            return;
          } else {
            setIsOpen(false);
            setIsModalOpen(false);
          }
        }}
        open={isOpen}
        position="bottom"
        showClose
        width={299}
      >
        <span id="changeLocation" style={{ display: 'none' }}>
          Change Location
        </span>
        <StyledLocationBox data-testid="provider-locations-box">
          <StyledLocationsDropdownOption>
            <TerminationBadge
              endDate={selectedLocation?.network?.end}
              message={t('PCP_TERMINATION.MAY_LEAVE_THE_NETWORK_ON')}
            />
            {displayInNetworkBadge && displayInNetworkBadgeEnabled && (
              <InNetworkBadgeWithDate
                message={t('DISPLAY_IN_NETWORK_BADGE.IN_NETWORK_BADGE_PCP')}
                startDate={selectedLocation?.network?.start}
              />
            )}
            {displayLocationName}
            <StyledAddressLine cssProps={{ isGehaTheme }}>
              {address.line}
            </StyledAddressLine>
            <StyledAddressLine cssProps={{ isGehaTheme }}>
              {address.city}, {isCountyLabelEnabled ? county : ''}{' '}
              {address.state} {postalCode}
            </StyledAddressLine>
            <StyledAddressLine cssProps={{ isGehaTheme }}>
              ({Number(distance).toFixed(1)} {t('PROVIDER_DETAILS.MILES')})
            </StyledAddressLine>
          </StyledLocationsDropdownOption>
          <IconSymbol
            color={isGehaTheme ? '#4c728a' : 'black'}
            data-auto-testid="provider-locations-dropdown"
            data-testid="provider-locations-dropdown"
            icon="keyboard_arrow_down"
          />
        </StyledLocationBox>
      </StyledLocationsPopover>
    </StyledLocationBoxContainer>
  ) : (
    <StyledLocationBox data-testid="provider-locations-box">
      <StyledLocationsDropdownOption>
        {displayLocationName}
        <StyledAddressLine cssProps={{ isGehaTheme }}>
          {address.line}
        </StyledAddressLine>
        <StyledAddressLine cssProps={{ isGehaTheme }}>
          {address.city}, {isCountyLabelEnabled ? county : ''} {address.state}{' '}
          {postalCode}
        </StyledAddressLine>
        <StyledAddressLine cssProps={{ isGehaTheme }}>
          ({Number(distance).toFixed(1)} {t('PROVIDER_DETAILS.MILES')})
        </StyledAddressLine>
      </StyledLocationsDropdownOption>
    </StyledLocationBox>
  );
};
